import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FolderTree, Plus, Edit2, Trash2, BarChart3 } from 'lucide-react';
import './CategoriesTab.css';

const CategoriesTab = ({ hotelId, token }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    category_name: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (hotelId && token) {
      fetchCategories();
    }
  }, [hotelId, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/categories?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const categoriesData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setCategories(categoriesData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingCategory) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/inventory/categories/${editingCategory.category_id}`,
          { ...formData, hotel_id: hotelId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Category updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/inventory/categories`,
          { ...formData, hotel_id: hotelId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Category created successfully');
      }
      
      fetchCategories();
      resetForm();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.message || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      category_name: category.category_name,
      description: category.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/inventory/categories/${categoryId}?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Category deleted successfully');
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setFormData({ category_name: '', description: '' });
    setEditingCategory(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="categories-tab">
      <div className="tab-header">
        <h2><FolderTree size={24} /> Categories</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={20} /> {showForm ? 'Cancel' : 'Add Category'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingCategory ? 'Edit Category' : 'New Category'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Category Name *</label>
              <input
                type="text"
                value={formData.category_name}
                onChange={(e) => setFormData({...formData, category_name: e.target.value})}
                required
                maxLength={100}
                placeholder="e.g., Cleaning Supplies, Food & Beverage"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                maxLength={500}
                rows={3}
                placeholder="Brief description of this category"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingCategory ? 'Update' : 'Create'} Category
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="categories-grid">
        {(Array.isArray(categories) ? categories : []).map((category) => (
          <div key={category.category_id} className="category-card">
            <div className="category-header">
              <FolderTree size={24} />
              <div className="category-actions">
                <button 
                  className="btn-icon" 
                  onClick={() => handleEdit(category)}
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="btn-icon btn-danger" 
                  onClick={() => handleDelete(category.category_id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <h3>{category.category_name}</h3>
            {category.description && <p>{category.description}</p>}
            
            <div className="category-stats">
              <div className="stat">
                <BarChart3 size={16} />
                <span>{category.item_count || 0} items</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="empty-state">
          <FolderTree size={48} />
          <h3>No Categories</h3>
          <p>Create your first category to organize inventory items</p>
        </div>
      )}
    </div>
  );
};

export default CategoriesTab;
